import { useQuery } from '@tanstack/react-query';
import { useTranslations, useLocale } from 'next-intl';
import { useRouter } from 'next/navigation';
import { useContext } from 'react';
import { AppConfigContext } from '@/features/app-providers/app-config-provider';
import { PageContext } from '@/features/app-providers/page-provider';
import { QueryApi } from '@/features/query/api';
import { PageTitle, replaceParams } from '@/schema/getStaticPageUrl';
import { TranslationLanguage } from '@/shared/i18n/constants';
import { localeToLang } from '@/shared/i18n/localeToLang';
import { toTranslationLanguage } from '@/shared/i18n/toTranslationLanguage';
import { Language } from '@/shared/model/Language';
import { getLocalizedValue } from '../getLocalizedValue';

export const useChangeLang = () => {
    const routes = useContext(AppConfigContext).allRoutes;
    const locale = useLocale();
    const router = useRouter();
    const t = useTranslations();
    const { pageName, params } = useContext(PageContext);
    const { data: venue } = useQuery(QueryApi.Venue.queries.Venue);
    const languageOptions = (venue?.languages ?? []).map((lang) => ({
        flag: TranslationLanguage[lang as keyof typeof TranslationLanguage],
        label: t(`LANGUAGES.${lang as keyof typeof TranslationLanguage}`),
        shortCut: lang,
    }));

    return {
        languageOptions,
        selectedLang:
            languageOptions.find((option) => locale && option.shortCut === localeToLang(locale.toString())) ||
            languageOptions[0],
        handleChangeLang: (newLang: Language) => {
            const language = toTranslationLanguage(newLang);

            if (locale === language) {
                return;
            }

            const route = pageName ? routes?.[pageName as PageTitle] : undefined;
            const newPath = `/${language}${replaceParams(getLocalizedValue(route, newLang, true), params)}`;
            router.replace(newPath);
        },
    };
};
